import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { Grid } from '@achieve/ascend'
import { Carousel } from 'components/Carousels'
import { Eyebrow } from 'components/Eyebrow'
import { AchieveLink } from 'components/AchieveLink'
import { getSlug } from 'utils/conversions'
import { AuthorAvatar } from 'components/AuthorAvatar'
import { AUTHORS_ROUTE, LEARN_ROUTE } from 'constants/navigation'
import { PlayButtonBackground } from 'components/PlayButtonBackground'
import classNames from 'classnames'
import { mainProductConfig } from 'services/contentful/helpers'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'

/**
 * Renders the horizontal especific list of articles
 *
 * @component
 * @param {Object} props.title - The Title of section list.
 * @param {Object} props.relatedArticles - The List of articles. accept query from contenfull client, or graphl structure data
 * @returns {JSX.Element} The rendered component. This component will render a list with a Title and List of articles(three) on desktop and carousel on mobile.
 *
 */
function ArticleListCarousel({ title = 'Related Articles', relatedArticles, linkCta, config }) {
  function RelatedArticleCard({ item, index }) {
    const {
      title,
      slug: articleSlug,
      primaryCategory,
      author,
      mainImage,
      metaDescription,
    } = item?.fields ?? item ?? {}
    const { state: { siteEnv } = {} } = useContext(LayoutContext)
    const { slug: authorSlug, authorName, authorPhoto } = author?.fields ?? author ?? {}
    const isVideo = mainImage?.__typename === 'VideoPlayer'

    const linkHrefArticle = `${LEARN_ROUTE}/${getSlug(primaryCategory)}/${articleSlug}`.replace(
      '//',
      '/'
    )
    const hrefCategory = `${LEARN_ROUTE}/${getSlug(primaryCategory)}`

    const LinkItem = ({ children, 'data-testid': dataTestId }) => {
      return (
        <AchieveLink
          href={linkHrefArticle}
          track={{
            list_name: `ARTICLE LINK`,
            position: index,
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | ${title}`,
            click_text: title,
            click_url: articleSlug,
            nav_link_section: 'Article',
            track_event: 'link_click',
            click_type: 'Link Click',
            event_action: 'link_click',
            event_type: 'link_click',
          }}
          className="text-[#1d252f]"
          data-testid={dataTestId || 'related-article-card-link'}
        >
          {children}
        </AchieveLink>
      )
    }

    const eyebrowsBlue = config?.eyebrowsBlue || false

    return (
      <Grid
        key={`releated-article-${index}`}
        container
        spacing={1}
        className="max-w-[335px] lg:max-w-[initial]"
        data-testid={`related-article-carousel-card-${index}`}
      >
        {mainImage && (
          <Grid item xs={12} className="min-h-[150px] max-h-[220px]">
            <div
              className={
                'relative max-h-full max-w-full w-[335px] h-[210px] lg:w-[390px] lg:h-[219px] [&>span]:box-border [&>span]:block [&>span]:overflow-hidden [&>span]:w-[initial] [&>span]:h-[initial] [&>span]:opacity-100 [&_span]:absolute [&_span]:m-0 [&_span]:p-0 [&_span]:border-0 [&_span]:inset-0 [&_span]:[background:none] [&_img]:[&_a]:absolute [&_img]:[&_a]:box-border [&_img]:[&_a]:block [&_img]:[&_a]:w-0 [&_img]:[&_a]:h-0 [&_img]:[&_a]:min-w-full [&_img]:[&_a]:max-w-full [&_img]:[&_a]:min-h-full [&_img]:[&_a]:max-h-full [&_img]:[&_a]:object-cover[&_img]:[&_a]:object-[center_top] [&_img]:[&_a]:m-auto [&_img]:[&_a]:p-0 [&_img]:[&_a]:rounded-xl [&_img]:[&_a]:border-[none] [&_img]:[&_a]:inset-0'
              }
            >
              <LinkItem data-testid="related-article-card-image-link">
                <>
                  <MediaImageStandard
                    content={
                      mainImage?.fields?.mediaContent ??
                      mainImage?.mediaContent ??
                      mainImage.thumbnail
                    }
                    data-testid="related-article-card-image"
                  />
                  {isVideo && <PlayButtonBackground fontSize={64} />}
                </>
              </LinkItem>
            </div>
          </Grid>
        )}
        <Grid item xs={12}>
          <AchieveLink
            href={hrefCategory}
            className={classNames({ 'text-[#3b6af7]': eyebrowsBlue })}
            data-testid="related-article-card-eyebrow-link"
          >
            <Eyebrow
              className={classNames('text-left pt-2 pb-0', { 'text-[#3b6af7]': eyebrowsBlue })}
              black
              content={primaryCategory}
              data-testid="related-article-card-eyebrow-text"
            />
          </AchieveLink>
        </Grid>
        {title && (
          <Grid item xs={12}>
            <LinkItem data-testid="related-article-card-title-link">
              <Typography
                content={title}
                variant="headingMd"
                fontWeight="medium"
                data-testid="related-article-card-title"
              />
            </LinkItem>
          </Grid>
        )}
        {!config?.hiddenDescription && (
          <Grid item xs={12} marginBottom={1}>
            <Typography
              content={metaDescription}
              variant="bodySm"
              data-testid="related-article-card-description"
            />
          </Grid>
        )}
        {authorName && (
          <Grid item xs={12} data-testid="related-article-card-author-container">
            <AuthorAvatar
              authorPhoto={authorPhoto}
              index={0}
              href={`${AUTHORS_ROUTE}/` + (authorSlug || getSlug(authorName))}
              authorName={authorName}
              priority={false}
            />
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <Grid container item xs={12} gap={2} paddingTop={4} paddingBottom={4}>
      {title && (
        <Grid item xs={12} marginBottom={2}>
          <Typography content={title} variant="headingMd" />
        </Grid>
      )}
      <Grid item xs={12} display={{ xs: 'block', lg: 'none' }}>
        <Carousel
          carouselItems={relatedArticles}
          className="p-0"
          classNameContainer={'gap-6 first:pl-[2px]'}
          classNameItems="max-w-[335px]"
          itemComponent={RelatedArticleCard}
          variant="standard"
          idPrefix={`releated-articles`}
          withIndicators={true}
          classNameIndicatorDots="bg-[#3300ff] mt-8"
        />
      </Grid>
      <Grid item xs={12} display={{ xs: 'none', lg: 'block' }}>
        <Grid container spacing={4}>
          {relatedArticles?.map((item, index) => {
            return (
              <Grid item xs={4} key={index}>
                <RelatedArticleCard item={item} index={index} />
              </Grid>
            )
          })}
        </Grid>
      </Grid>
      {linkCta && (
        <Grid item xs={12} marginBottom={2} className="flex justify-center mt-8 mb-0 mx-0">
          <LinkButton
            size="large"
            content={linkCta}
            data-testid="article-cta"
            typographicOptions={{ variant: 'labelMd' }}
            className="w-full lg:w-auto"
          />
        </Grid>
      )}
    </Grid>
  )
}

export { ArticleListCarousel }
export default ArticleListCarousel
